import React, { useState, useEffect, useRef } from 'react';
import Header from '../components/header';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSearch } from '@fortawesome/free-solid-svg-icons';
import BaseURL from '../assets/contants/BaseURL';
import axios from 'axios';
import '../scss/Proreport.scss';
import * as ExcelJS from 'exceljs';

const inputStyle = {
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#333',
    backgroundColor: '#f9f9f9',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'border-color 0.3s ease',
    width: '150px',
};

const ActualReadingReports = () => {
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [reportData, setReportData] = useState({ daily_data: [], totals: {} });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const handleSearchClick = async () => {
        if (!fromDate || !toDate) {
            setError('Please select both from and to dates');
            return;
        }

        if (new Date(fromDate) > new Date(toDate)) {
            setError('From date cannot be after To date');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await axios.post(`${BaseURL}meters/actual-readings-report/`, {
                from_date: fromDate,
                to_date: toDate
            });
            setReportData(response.data);
            setCurrentPage(1);
        } catch (err) {
            console.error('Error fetching report data:', err);
            setError('Failed to fetch report data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const totalItems = reportData.daily_data.length;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const paginatedData = reportData.daily_data.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const downloadExcel = async () => {
        if (!reportData || !reportData.daily_data.length) {
            alert('No data available to download');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Actual Readings Report');

        const getBase64FromImageUrl = async (url) => {
            const response = await fetch(url);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result.split(',')[1]);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        };
        worksheet.properties.defaultColWidth = 20;

        const columns = [
            { header: 'Date', key: 'date', width: 20 },
            { header: 'T1', key: 't1', width: 20 },
            { header: 'T2', key: 't2', width: 20 },
            { header: 'T3', key: 't3', width: 20 },
            { header: 'T4', key: 't4', width: 20 },
            { header: 'T5', key: 't5', width: 20 },
            { header: 'Total (T)', key: 't', width: 20 }
        ];
        const columnCount = columns.length;

        for (let rowNum = 1; rowNum <= 3; rowNum++) {
            const row = worksheet.getRow(rowNum);
            for (let i = 1; i <= columnCount; i++) {
                row.getCell(i).fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFDDDDDD' },
                };
            }
        }

        try {
            const logoBase64 = await getBase64FromImageUrl('/brad.png');
            const logoId = workbook.addImage({
                base64: logoBase64,
                extension: 'png'
            });

            const centerCol = Math.floor(columnCount / 2) - 1;
            worksheet.addImage(logoId, {
                tl: { col: centerCol + 1, row: 0 },
                br: { col: centerCol + 2, row: 3 },
                editAs: 'oneCell'
            });

            worksheet.getRow(1).height = 30;
            worksheet.getRow(2).height = 30;
            worksheet.getRow(3).height = 30;
        } catch (error) {
            console.error('Error adding logo:', error);
        }

        const dateRow = worksheet.addRow([`From Date: ${fromDate || ''}, To Date: ${toDate || ''}`]);
        worksheet.mergeCells(dateRow.number, 1, dateRow.number, columnCount);
        dateRow.font = { bold: true, size: 14 };
        dateRow.alignment = { horizontal: 'center', vertical: 'middle' };
        dateRow.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD9EAD3' },
            };
        });

        const headerRow = worksheet.addRow(columns.map(col => col.header));
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFA500' },
            };
        });

        reportData.daily_data.forEach((day, rowIndex) => {
            const rowData = {
                date: day.date,
                t1: day.slots.t1.toFixed(2),
                t2: day.slots.t2.toFixed(2),
                t3: day.slots.t3.toFixed(2),
                t4: day.slots.t4.toFixed(2),
                t5: day.slots.t5.toFixed(2),
                t: day.slots.t.toFixed(2)
            };

            const dataRow = worksheet.addRow(Object.values(rowData));
            dataRow.height = 30;
            dataRow.eachCell(cell => {
                const fillColor = rowIndex % 2 === 0 ? 'FFE8EAF6' : 'FFFFFFFF';
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: fillColor },
                };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
        });

        const totalsRowData = ['TOTAL',
            reportData.totals.t1.toFixed(2),
            reportData.totals.t2.toFixed(2),
            reportData.totals.t3.toFixed(2),
            reportData.totals.t4.toFixed(2),
            reportData.totals.t5.toFixed(2),
            reportData.totals.t.toFixed(2)
        ];

        const totalsRow = worksheet.addRow(totalsRowData);
        totalsRow.font = { bold: true };
        totalsRow.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFEB3B' },
            };
            cell.alignment = { horizontal: 'right' };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };
        });

        const footerRow = worksheet.addRow(['Powered by Innospace']);
        footerRow.font = { bold: true, size: 12 };
        footerRow.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.mergeCells(footerRow.number, 1, footerRow.number, columnCount);
        footerRow.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFDDDDDD' },
            };
        });

        worksheet.columns.forEach(column => {
            column.width = 20;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Actual_Readings_${fromDate}_to_${toDate}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div style={{ textAlign: 'center', width: '100%' }}>
            <div style={{
                backgroundColor: '#007acc',
                color: 'white',
                alignContent: 'center',
                fontSize: '14px',
                height: '40px'
            }}>
                <Header title="TOD GRID REPORTS" />
            </div>
            <div style={{
                border: '1px solid #ccc',
                padding: '10px',
                marginTop: '20px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: 'white',
                maxWidth: '980px',
                marginLeft: 'auto',
                marginRight: 'auto',
                borderRadius: '8px'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <label style={{ fontWeight: 'bold' }}>From Date:</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            style={inputStyle}
                            max={new Date().toISOString().split('T')[0]}
                        />

                        <label style={{ fontWeight: 'bold' }}>To Date:</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            style={inputStyle}
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    <button
                        style={{
                            padding: '8px 12px',
                            backgroundColor: '#007acc',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            marginLeft: '-250px'
                        }}
                        onClick={handleSearchClick}
                    >
                        <FontAwesomeIcon icon={faSearch} />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px 10px',
                                backgroundColor: '#007acc',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '8px',
                                width: '95px'
                            }}
                            onClick={downloadExcel}
                            disabled={!reportData || loading}
                        >
                            <span>Download</span>
                            <FontAwesomeIcon icon={faDownload} style={{ marginLeft: '5px' }} />
                        </button>
                    </div>
                </div>
                {error && <p style={{ color: 'red', textAlign: 'center', marginTop: '10px' }}>{error}</p>}
            </div>

            <div className="dashboards">
                <div className="Monthly-Reports">
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr style={{ backgroundColor: '#007acc', color: 'white' }}>
                                    <th>S.No</th>
                                    <th>Date</th>
                                    <th>Slot T1</th>
                                    <th>Slot T2</th>
                                    <th>Slot T3</th>
                                    <th>Slot T4</th>
                                    <th>Slot T5</th>
                                    <th>Total (T)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center' }}>
                                            Loading data...
                                        </td>
                                    </tr>
                                ) : paginatedData.length > 0 ? (
                                    <>
                                        {paginatedData.map((day, index) => (
                                            <tr key={index}>
                                                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                <td>{day.date}</td>
                                                <td>{day.slots.t1.toFixed(2)}</td>
                                                <td>{day.slots.t2.toFixed(2)}</td>
                                                <td>{day.slots.t3.toFixed(2)}</td>
                                                <td>{day.slots.t4.toFixed(2)}</td>
                                                <td>{day.slots.t5.toFixed(2)}</td>
                                                <td>{day.slots.t.toFixed(2)}</td>
                                            </tr>
                                        ))}
                                        <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                                            <td colSpan="2">TOTALS</td>
                                            <td>{reportData.totals.t1.toFixed(2)}</td>
                                            <td>{reportData.totals.t2.toFixed(2)}</td>
                                            <td>{reportData.totals.t3.toFixed(2)}</td>
                                            <td>{reportData.totals.t4.toFixed(2)}</td>
                                            <td>{reportData.totals.t5.toFixed(2)}</td>
                                            <td>{reportData.totals.t.toFixed(2)}</td>
                                        </tr>
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan="8" style={{ textAlign: 'center' }}>
                                            {reportData.daily_data.length === 0 ? 'No data available' : 'Select date range and click search'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <div style={{ textAlign: "center", marginTop: "20px" }}>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{
                                    margin: "0 5px",
                                    padding: "5px 10px",
                                    background: currentPage === 1 ? "#ddd" : "#007acc",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "25px",
                                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                                }}
                            >
                                Previous
                            </button>

                            {[...Array(totalPages).keys()].map((_, i) => (
                                <button
                                    key={i + 1}
                                    onClick={() => handlePageChange(i + 1)}
                                    style={{
                                        margin: "0 5px",
                                        padding: "5px 10px",
                                        background: currentPage === i + 1 ? "#007acc" : "#ddd",
                                        color: currentPage === i + 1 ? "#ffffff" : "#333",
                                        border: "1px solid #ddd",
                                        borderRadius: "25px",
                                        cursor: "pointer",
                                    }}
                                >
                                    {i + 1}
                                </button>
                            ))}

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{
                                    margin: "0 5px",
                                    padding: "5px 10px",
                                    background: currentPage === totalPages ? "#ddd" : "#007acc",
                                    color: "#ffffff",
                                    border: "none",
                                    borderRadius: "25px",
                                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                                }}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActualReadingReports;